import React, { Fragment, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

const ReactTooltip = dynamic(() => import('react-tooltip'), { ssr: false });

const yAxisLabelItem = PropTypes.shape({
  label: PropTypes.string,
  industrie: PropTypes.string,
  isLinkIndexed: PropTypes.bool,
});

const propTypes = {
  tooltipKey: PropTypes.string.isRequired,
  yAxisLabels: PropTypes.oneOfType([
    PropTypes.arrayOf(yAxisLabelItem),
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  xAxisLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  values: PropTypes.arrayOf(PropTypes.number).isRequired,
  compareValues: PropTypes.arrayOf(PropTypes.number).isRequired,
  yAxisLabelsWidth: PropTypes.string.isRequired,
  jobTitle: PropTypes.string.isRequired,
  compareJobTitle: PropTypes.string.isRequired,
  showMoreButton: PropTypes.bool,
  showMoreButtonCallback: PropTypes.func,
  scale: PropTypes.number,
  calculateBarWidth: PropTypes.func,
};

const HorizontalBarChart = (props) => {
  const {
    tooltipKey,
    xAxisLabels,
    compareValues,
    yAxisLabelsWidth,
    jobTitle,
    compareJobTitle,
    showMoreButton,
    showMoreButtonCallback,
    scale = 1,
    calculateBarWidth,
    formatTooltip,
    xLabelFromRight = 0,
  } = props;

  const DEFAULT_ROWS_AMOUNT_FOR_SHOW_MORE = 5;

  const BAR_CHART_COLORS = {
    primary: '#26a69a',
    secondary: '#d6d9db',
  };

  let BAR_CHART_VALUES;

  useEffect(() => {
    BAR_CHART_VALUES = document.getElementsByClassName('bar-chart-value');
  });
  const [showMore, setShowMore] = useState(showMoreButton);

  let yAxisLabels = props.yAxisLabels;
  let values = props.values;

  // remove zero percentage categories from company types chart
  if (tooltipKey && tooltipKey === 'companyTypes') {
    yAxisLabels = yAxisLabels.filter((label, idx) => values[idx] !== 0);
    values = values.filter((value) => value !== 0);
  }

  const compareValuePresent = compareValues && compareValues.length;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const xAxisLabelsRefs = Array.from({ length: xAxisLabels.length }, () => useRef(null));

  const onBarMouseOver = ({ target }) => {
    const nextElementSibling = target.nextElementSibling;
    const prevElementSibling = target.previousElementSibling;
    for (const bar of BAR_CHART_VALUES) {
      if (target !== bar && bar !== prevElementSibling && bar !== nextElementSibling) {
        bar.classList.add('highlighted');
      }
    }
  };

  const onBarMouseLeave = () => {
    for (const bar of BAR_CHART_VALUES) {
      bar.classList.remove('highlighted');
    }
  };

  const handleShowMoreButtonClick = () => {
    if (showMoreButtonCallback) {
      showMoreButtonCallback();
    }
    setShowMore(false);
  };

  const renderTooltip = (idx, value, compareValue) => {
    const tooltipContent = compareValuePresent ? (
      <Fragment>
        {compareValue ? (
          <p className="custom-tooltip-text">
            <span
              style={{ background: BAR_CHART_COLORS.primary }}
              className="custom-tooltip-label-circle value"
            />
            {compareJobTitle}: <label className="custom-tooltip-label">{compareValue}%</label>
          </p>
        ) : null}
        {value ? (
          <p className="custom-tooltip-text">
            <span
              style={{ background: BAR_CHART_COLORS.secondary }}
              className="custom-tooltip-label-circle compare-value"
            />
            {formatTooltip ? (
              formatTooltip(value)
            ) : (
              <>
                {jobTitle}: <label className="custom-tooltip-label">{value}%</label>
              </>
            )}
          </p>
        ) : null}
      </Fragment>
    ) : formatTooltip ? (
      formatTooltip(value)
    ) : (
      <>
        <p className="custom-tooltip-text">
          {jobTitle}: <label className="custom-tooltip-label">{value}%</label>
        </p>
      </>
    );

    return (
      <ReactTooltip
        id={`tooltip-${idx}-${tooltipKey}`}
        className="custom-tooltip-styles"
        getContent={() => tooltipContent}
      />
    );
  };

  const renderXAxisGrid = () => (
    <div style={{ flexBasis: `calc(100% - ${yAxisLabelsWidth})` }} className="bar-chart-grid">
      {xAxisLabels.map((gridItemLabel, idx, self) => (
        <div
          key={idx}
          style={{ flexBasis: `${100 / (self.length - 1)}%` }}
          className="bar-chart-grid-item"
        >
          <label
            ref={xAxisLabelsRefs[idx]}
            style={{
              right: `-${
                xAxisLabelsRefs[idx].current
                  ? xAxisLabelsRefs[idx].current.offsetWidth / 2
                  : xLabelFromRight
              }px`,
            }}
            className="bar-chart-grid-item-label"
          >
            {gridItemLabel}
          </label>
        </div>
      ))}
    </div>
  );

  const renderCompareLegends = () => {
    if (compareValuePresent) {
      return (
        <div style={{ paddingLeft: yAxisLabelsWidth }} className="compare-legends">
          <div className="legend-wrapper">
            <span
              style={{ background: BAR_CHART_COLORS.primary }}
              className="legend-wrapper-circle primary"
            />
            {compareJobTitle}
          </div>
          <div className="legend-wrapper">
            <span
              style={{ background: BAR_CHART_COLORS.secondary }}
              className="legend-wrapper-circle secondary"
            />
            {jobTitle}
          </div>
        </div>
      );
    }
    return null;
  };

  const isLastItem = (index, itemsLength) => {
    return showMore && index === DEFAULT_ROWS_AMOUNT_FOR_SHOW_MORE - 1
      ? 'last'
      : index === itemsLength - 1
      ? 'last'
      : '';
  };

  return (
    <Fragment>
      {renderCompareLegends()}
      <div className="bar-chart-container">
        <div
          style={yAxisLabelsWidth ? { flexBasis: yAxisLabelsWidth } : {}}
          className="bar-chart-labels-container"
        >
          {yAxisLabels
            .map((label, idx) => (
              <div className={`bar-chart-label ${isLastItem(idx, yAxisLabels.length)}`} key={idx}>
                {label?.isLinkIndexed ? (
                  <a className="label-text line-clamp-2" href={label?.link}>
                    {label?.industrie}
                  </a>
                ) : (
                  <p className="label-text line-clamp-2">{label?.industrie ?? label}</p>
                )}
              </div>
            ))
            .filter((_, idx) => idx < (showMore ? DEFAULT_ROWS_AMOUNT_FOR_SHOW_MORE : Infinity))}

          {showMore && (
            <div className="list-link show-more-button" onClick={handleShowMoreButtonClick}>
              Show More
            </div>
          )}
        </div>
        <div
          style={yAxisLabelsWidth ? { flexBasis: `calc(100% - ${yAxisLabelsWidth})` } : {}}
          className="bar-chart-data-container"
        >
          {renderXAxisGrid()}
          {values
            .map((value, idx) => (
              <Fragment key={idx}>
                <div className={`bar-chart-value-wrapper ${isLastItem(idx, values.length)}`}>
                  {compareValuePresent ? (
                    <div
                      style={{
                        width: `${compareValues[idx]}%`,
                        background: BAR_CHART_COLORS.primary,
                      }}
                      onMouseOver={(e) => onBarMouseOver(e)}
                      onMouseLeave={() => onBarMouseLeave()}
                      className="bar-chart-value compare-value"
                    />
                  ) : null}
                  <div
                    style={{
                      width: calculateBarWidth
                        ? `${calculateBarWidth(value)}%`
                        : `${value * scale}%`,
                      height: `${compareValuePresent ? '10px' : '24px'}`,
                      background: compareValuePresent
                        ? BAR_CHART_COLORS.secondary
                        : BAR_CHART_COLORS.primary,
                    }}
                    onMouseOver={(e) => onBarMouseOver(e)}
                    onMouseLeave={() => onBarMouseLeave()}
                    className="bar-chart-value"
                  />
                  <div
                    data-tip
                    data-for={`tooltip-${idx}-${tooltipKey}`}
                    style={{
                      width: `${
                        compareValues[idx] >= value
                          ? compareValues[idx]
                          : calculateBarWidth
                          ? calculateBarWidth(value)
                          : value * scale
                      }%`,
                    }}
                    className="tooltip-paceholder"
                  />
                </div>
                {renderTooltip(idx, value, compareValues[idx])}
              </Fragment>
            ))
            .filter((_, idx) => idx < (showMore ? DEFAULT_ROWS_AMOUNT_FOR_SHOW_MORE : Infinity))}
        </div>
      </div>
    </Fragment>
  );
};

HorizontalBarChart.propTypes = propTypes;

export default HorizontalBarChart;
